/* eslint-disable */
import React, { Component } from "react";
import moment from 'moment-timezone';
import { capitalizeFirstLetter, formatMinutesToHHMMSS } from "../../utils/CommonUtils";
import { convertWebinarTime, getWebinarMoment } from "../../utils/TimezoneUtils";
import WebinarRESTService from "../../services/WebinarRESTService";
import SpinnerButton from "./SpinnerButton";
import sanitizeHtml from 'sanitize-html';
import AccessLabel from "./AccessLabel";
import WebinarTags from "./WebinarTags";

/**
 * Mirrors CANONICAL_EXAM_GROUPS from backend/features/webinars/utils/ExamUtils.js
 * Each entry: { label: display name, keys: all exam_tag values that belong to this group }
 * Order matters — III before II before I to avoid substring false matches.
 */
const CANONICAL_EXAM_DISPLAY = [
    { label: 'CFA Level III', keys: ['cfa_3', 'cfa_3_2025', 'cfa_3_2026'] },
    { label: 'CFA Level II', keys: ['cfa_2', 'cfa_2_2022', 'cfa_2_2023', 'cfa_2_2025', 'cfa_2_2026', 'cfa_2_2027'] },
    { label: 'CFA Level I', keys: ['cfa_1', 'cfa', 'cfa_2022', 'cfa_2023', 'cfa_2024', 'cfa_2025', 'cfa_2026'] },
    { label: 'FRM Part I & Part II', keys: ['frm_1_2'] },
    { label: 'FRM Part II', keys: ['frm_2'] },
    { label: 'FRM Part I', keys: ['frm'] },
    { label: 'SOA P & FM', keys: ['soa_p_fm_ifm'] },
    { label: 'SOA IFM', keys: ['soa_ifm'] },
    { label: 'SOA FM', keys: ['soa_fm'] },
    { label: 'SOA P', keys: ['soa_p'] },
    { label: 'SOA', keys: ['soa'] },
    { label: 'GMAT Focus', keys: ['gmat_focus'] },
    { label: 'GMAT', keys: ['gmat'] },
    { label: 'EA', keys: ['ea'] },
    { label: 'ESG', keys: ['esg'] },
];

function getExamDisplayLabel(exams) {
    if (!exams || exams.length === 0) return null;
    const examTags = exams.map(e => String(e.exam_tag || '').toLowerCase());
    for (const group of CANONICAL_EXAM_DISPLAY) {
        if (group.keys.some(k => examTags.includes(k))) return group.label;
    }
    return null;
}

function getRegionChipClass(region) {
    if (!region) return '';
    const key = region.toLowerCase();
    return `webinar-card__chip--region-${key}`;
}

class WebinarsListItem extends Component {

    constructor(props, context) {
        super(props, context);
        this.state = {
            actionLoading: false,
            showModal: false,
        };
    }

    componentDidMount() {
        this._startCountdownTimer();
    }

    componentWillUnmount() {
        if (this._countdownInterval) {
            clearInterval(this._countdownInterval);
        }
    }

    _startCountdownTimer() {
        // Live sessions tick every second for seconds precision;
        // upcoming sessions tick every minute
        const { sessionState } = this.props.item;
        const interval = sessionState === 'live' ? 1000 : 60000;
        this._countdownInterval = setInterval(() => {
            this.forceUpdate();
        }, interval);
    }

    /**
     * Returns a countdown/status string based on session state:
     *  - Upcoming:  "3d 2h 15m"
     *  - Live now:  "1h 30m left"
     *  - Ended:     "Ended"
     */
    getCountdown = () => {
        const { datetimeStart, datetimeStartTz, datetimeEnd, datetimeEndTz } = this.props.item;
        if (!datetimeStart) return null;
        const now = moment();
        const start = getWebinarMoment(datetimeStart, datetimeStartTz);
        const end = datetimeEnd ? getWebinarMoment(datetimeEnd, datetimeEndTz) : null;

        // Session ended
        if (end && end.diff(now) <= 0) return 'Ended';

        // Session is live — show time remaining with seconds
        if (start.diff(now) <= 0 && end && end.diff(now) > 0) {
            const remaining = moment.duration(end.diff(now));
            const hours = Math.floor(remaining.asHours());
            const minutes = remaining.minutes();
            const seconds = remaining.seconds();
            const parts = [];
            if (hours > 0) parts.push(`${hours}h`);
            parts.push(`${minutes}m`);
            parts.push(`${seconds}s`);
            return parts.join(' ') + ' left';
        }

        // Upcoming — show countdown to start
        const diff = start.diff(now);
        if (diff <= 0) return null;
        const duration = moment.duration(diff);
        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const minutes = duration.minutes();
        return `${days}d ${hours}h ${minutes}m`;
    }

    __setLoadingDeferred = (state, time = 800) => {
        setTimeout(() => {
            this.setState({ actionLoading: state }, () => { })
        }, time);
    }

    __setLoadingAsync = (value) => {
        return new Promise((resolve, reject) => {
            this.setState({ actionLoading: value }, () => {
                resolve();
            })
        });
    }

    __testTimeout = () => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve(true);
            }, 10000);
        })
    }

    handleRegister = async () => {
        try {
            if (this.props.item == null || this.props.item.id == null || this.props.userData == null || this.props.userData.token == null) {
                throw new Error("Webinar id is null");
            }
            await this.__setLoadingAsync(true);
            const requestData = {
                id: this.props.item.id,
                preferredTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                preferredTimezoneOffset: new Date().getTimezoneOffset(),
                preferredLanguage: navigator.language,
            }
            await new WebinarRESTService().setAuthHeader(this.props.userData.token).createRegistration(requestData);

            if (this.props.onWebinarUpdate) {
                await this.props.onWebinarUpdate(this.props.item.id);
            }
        } catch (e) {
            console.log("Error in handleRegister: ", e);
        } finally {
            this.__setLoadingDeferred(false);
        }
    }

    handleCancelClick = () => {
        if (window.confirm("Are you sure you want to cancel registration on this webinar?")) {
            this.handleCancel();
        }
    }

    handleCancel = async () => {
        try {
            if (this.props.item == null || this.props.item.user_registration == null || this.props.userData == null || this.props.userData.token == null) {
                throw new Error("Cannot cancel: missing registration data");
            }
            await this.__setLoadingAsync(true);
            const registrationId = this.props.item.user_registration.id;
            await new WebinarRESTService().setAuthHeader(this.props.userData.token).cancelRegistration({ id: registrationId });
            if (this.props.onWebinarUpdate) {
                await this.props.onWebinarUpdate(this.props.item.id);
            }
        } catch (e) {
            console.log("Error in handleCancel: ", e);
        } finally {
            this.__setLoadingDeferred(false);
        }
    }

    createMarkup(html) {
        return {
            __html: sanitizeHtml(html || '', {
                allowedTags: [
                    'p', 'br', 'b', 'strong', 'i', 'em', 'u',
                    'ul', 'ol', 'li',
                    'a', 'span', 'div'
                ],
                allowedAttributes: {
                    a: ['href', 'target', 'rel'],
                    span: ['class'],
                    div: ['class']
                },
                allowedSchemes: ['http', 'https', 'mailto']
            })
        };
    }

    //TODO: Extend on other exams
    makeUpgradeUrlFromExams() {
        const { exams } = this.props.item;
        if (exams == null || exams.length === 0) return null;
        function findPrimaryExam(relatedExams = []) {
            const exams = relatedExams
                .filter(item => item && item.exam_tag && item.exam_id)
                .map(item => ({
                    exam_id: item.exam_id,
                    exam_tag: String(item.exam_tag).trim().toLowerCase(),
                }));

            return (
                exams.find(x => x.exam_tag === 'cfa') ||
                exams.find(x => x.exam_tag === 'cfa_2') ||
                exams.find(x => x.exam_tag === 'cfa_3') ||
                exams.find(x => /^cfa(?:_\d{4})?$/.test(x.exam_tag)) ||
                exams.find(x => /^cfa_2(?:_\d{4})?$/.test(x.exam_tag)) ||
                exams.find(x => /^cfa_3(?:_\d{4})?$/.test(x.exam_tag)) ||
                exams[0] ||
                null
            );
        }

        function normalizeExamTagForSubscribe(tag = '') {
            const examTag = String(tag).trim().toLowerCase();

            // Map DB exam_name variants to the canonical UI keys used by
            // /subscribe/selectPackage/:uiKey/:examId (see backend
            // canonicalExamGroupCheck.js UI_TO_DB_MATCH).
            if (/^cfa_2(?:_\d{4})?$/.test(examTag)) return 'cfa2';
            if (/^cfa_3(?:_\d{4})?$/.test(examTag)) return 'cfa3';
            if (/^cfa(?:_\d{4})?$/.test(examTag)) return 'cfa1';

            return examTag;
        }

        function buildSubscribeUrl(relatedExams = []) {
            const exam = findPrimaryExam(relatedExams);
            if (!exam) return null;

            const normalizedTag = normalizeExamTagForSubscribe(exam.exam_tag);
            return `/subscribe/selectPackage/${normalizedTag}/${exam.exam_id}`;
        }
        return buildSubscribeUrl(exams);
    }

    renderActionButton = () => {
        const { user_registration, seatsTotal, seatsTaken, accessibility } = this.props.item;
        const { actionLoading } = this.state;
        const isRegistered = user_registration != null;
        const registeredStatus = isRegistered ? user_registration.registration_status : 'unknown';
        const isFull = seatsTotal > 0 && seatsTaken >= seatsTotal;
        const countdown = this.getCountdown();

        if (accessibility === false) {
            const url = this.makeUpgradeUrlFromExams();
            return (
                <div className="webinar-card__actions">
                    <a href={url}
                        className="webinar-card__button webinar-card__button--upgrade"
                        disabled
                    >
                        Upgrade to register
                    </a>
                </div>
            );
        }

        // Session full and not registered
        if (!isRegistered && isFull) {
            return (
                <div className="webinar-card__actions">
                    <SpinnerButton
                        className="webinar-card__button webinar-card__button--full"
                        disabled
                    >
                        Session Full
                    </SpinnerButton>
                </div>
            );
        }

        // Registered — status box with "Open in Teams" button
        if (registeredStatus === 'registered') {
            const joinUrl = user_registration.joinUrl;
            return (
                <div className="webinar-card__status-box webinar-card__status-box--registered">
                    <div className="webinar-card__status-header">
                        <span className="webinar-card__status-text webinar-card__status-text--registered">
                            <svg className="webinar-card__status-icon" viewBox="0 0 20 20" fill="#22c55e" width="16" height="16">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Registered
                        </span>
                        {countdown && <span className="webinar-card__countdown">{countdown}</span>}
                    </div>
                    {joinUrl && (
                        <a href={joinUrl} target="_blank" rel="noopener noreferrer" className="webinar-card__join-btn">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                            </svg>
                            Open in Teams
                        </a>
                    )}
                    <span className="webinar-card__cancel-link" onClick={!actionLoading ? this.handleCancelClick : undefined}>
                        {actionLoading ? 'Cancelling...' : 'Cancel registration'}
                    </span>
                </div>
            );
        }

        // Pending Approval — status box with info message
        if (registeredStatus === 'pendingApproval') {
            return (
                <div className="webinar-card__status-box webinar-card__status-box--pending">
                    <div className="webinar-card__status-header">
                        <span className="webinar-card__status-text webinar-card__status-text--pending">
                            {/*<svg className="webinar-card__status-icon" viewBox="0 0 20 20" fill="#d97706" width="16" height="16">*/}
                            {/*    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>*/}
                            {/*</svg>*/}
                            ⏳ Pending Approval
                        </span>
                        {countdown && <span className="webinar-card__countdown">{countdown}</span>}
                    </div>
                    <p className="webinar-card__status-message">Your join link will be available once approved.</p>
                    <span className="webinar-card__cancel-link" onClick={!actionLoading ? this.handleCancelClick : undefined}>
                        {actionLoading ? 'Cancelling...' : 'Cancel registration'}
                    </span>
                </div>
            );
        }

        // Unknown future value
        if (registeredStatus === 'unknownFutureValue') {
            return (
                <div className="webinar-card__actions">
                    <SpinnerButton
                        className="webinar-card__button webinar-card__button--future"
                        disabled
                    >
                        Pending
                    </SpinnerButton>
                </div>
            );
        }

        // Default — Register button
        const registerLoading = actionLoading;
        return (
            <div className="webinar-card__actions">
                <SpinnerButton
                    className="webinar-card__button"
                    onClick={this.handleRegister}
                    loading={registerLoading}
                >
                    {registerLoading ? 'Registering...' : 'Register'}
                </SpinnerButton>
            </div>
        );
    }

    render() {

        if (this.props.item == null) {
            return null;
        }
        const { accessibility, title, description, datetimeStart, datetimeStartTz, datetimeEnd, datetimeEndTz, durationMinutes, instructorName, presenters = [], tags, webinarStatus, user_registration, capacity, eligibilityLevel, raw_description, region, exams, sessionState } = this.props.item;

        const isLiveNow = sessionState === 'live';

        const cardClassName = [
            'webinar-card',
            isLiveNow ? 'webinar-card--live-now' : '',
        ].filter(Boolean).join(' ');

        // const accessLabel = eligibilityLevel != null && eligibilityLevel.length > 0 ? capitalizeFirstLetter(eligibilityLevel) : null;

        const filteredPresenters = presenters.length > 0 && presenters.filter(i => i !== instructorName);

        const listPresenters = filteredPresenters.length > 0 ? filteredPresenters.join(', ') : null;

        // const isRegistered = user_registration != null;
        // const registeredStatus = isRegistered ? user_registration.registration_status : 'unknown';

        const countdown = this.getCountdown();
        const durationTimes = durationMinutes != null && durationMinutes > 0 ? formatMinutesToHHMMSS(durationMinutes) : null;
        const startConverted = convertWebinarTime(datetimeStart, datetimeStartTz, 'ddd DD, MMM - h:mm A');
        const endConverted = convertWebinarTime(datetimeEnd, datetimeEndTz, 'h:mm A');
        const startDateFormat = startConverted.formatted;
        const endTimeFormat = endConverted.formatted;
        const tzAbbr = startConverted.tzAbbr;

        return (
            <article className={cardClassName}>
                <div className="webinar-card__header">
                    <div className="webinar-card__top">
                        <div className="webinar-card__badges-left">
                            {isLiveNow ? (
                                <span className="webinar-card__badge webinar-card__badge--live">
                                    <span className="webinar-card__badge-dot webinar-card__badge-dot--live" />
                                    LIVE NOW
                                </span>
                            ) : (
                                <span className="webinar-card__badge">
                                    <span className="webinar-card__badge-dot" />
                                    UPCOMING
                                </span>
                            )}
                            {getExamDisplayLabel(exams) && (
                                <span className="webinar-card__chip webinar-card__chip--exam">{getExamDisplayLabel(exams)}</span>
                            )}
                            {region && (
                                <span className={'webinar-card__chip webinar-card__chip--region ' + getRegionChipClass(region)}>{region || ""}</span>
                            )}
                        </div>
                        <AccessLabel accessibility={accessibility} eligibilityLevel={eligibilityLevel} />
                    </div>
                    <h3 className="webinar-card__title">{title}</h3>
                </div>


                <div className="webinar-card__body">
                    <div className="webinar-card__description">
                        {description}
                    </div>
                    {description && description.length > 0 && (
                        <span className="webinar-card__readmore" onClick={() => this.setState({ showModal: true })}>
                            Read more
                        </span>
                    )}
                </div>

                <div className="webinar-card__footer">

                    <div className="webinar-card__meta">
                        {isLiveNow && countdown ? (
                            <div className="webinar-card__live-remaining">
                                <span className="wb-svg-icon-16">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="12,6 12,12 16,14"></polyline>
                                    </svg>
                                </span>
                                <span>{countdown}</span>
                            </div>
                        ) : (
                            <div className="webinar-card__meta-item">
                                <span className="wb-svg-icon-16">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="#5a6a7a" strokeWidth="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2"></rect>
                                        <line x1="16" y1="2" x2="16" y2="6"></line>
                                        <line x1="8" y1="2" x2="8" y2="6"></line>
                                        <line x1="3" y1="10" x2="21" y2="10"></line>
                                    </svg>
                                </span>
                                <span>{startDateFormat} {tzAbbr}</span>
                            </div>
                        )}

                        {!isLiveNow && (
                            <div className="webinar-card__meta-item">
                                <span className="wb-svg-icon-16">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="#5a6a7a" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="12,6 12,12 16,14"></polyline>
                                    </svg>
                                </span>
                                <span>{durationTimes.hours}h {durationTimes.minutes}m </span>
                            </div>
                        )}
                    </div>

                    {instructorName != null && instructorName.length > 0 && instructorName.trim().length > 0 ? (
                        <div className="webinar-card__speaker">
                            {/*<span className="webinar-card__icon">👤</span>*/}
                            <span className="wb-svg-icon-16">
                                <svg viewBox="0 0 24 24" fill="none" stroke="#5a6a7a" strokeWidth="2">
                                    <path d="M20,21v-2a4,4,0,0,0-4-4H8a4,4,0,0,0-4,4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                            </span>
                            <span>{instructorName}</span>
                        </div>
                    ) : null}

                    {listPresenters != null && listPresenters.length > 0 ?
                        // listPresenters.map((presenter, index) => (
                        <div className="webinar-card__speaker">
                            <span className="wb-svg-icon-16">
                                <svg viewBox="0 0 24 24" fill="none" stroke="#5a6a7a" strokeWidth="2">
                                    <path d="M20,21v-2a4,4,0,0,0-4-4H8a4,4,0,0,0-4,4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                            </span>
                            <span>{listPresenters}</span>
                        </div>
                        // ))
                        : null}

                    <WebinarTags tags={tags} />
                    {this.renderActionButton()}
                </div>


                {this.state.showModal && (
                    <div className="webinar-modal__overlay" onClick={() => this.setState({ showModal: false })}>
                        <div className="webinar-modal__content" onClick={(e) => e.stopPropagation()}>
                            <button className="webinar-modal__close" onClick={() => this.setState({ showModal: false })} type="button">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                            {isLiveNow ? (
                                <span className="webinar-card__badge webinar-card__badge--live">
                                    <span className="webinar-card__badge-dot webinar-card__badge-dot--live" />
                                    LIVE NOW
                                </span>
                            ) : (
                                <span className="webinar-card__badge">
                                    <span className="webinar-card__badge-dot" />
                                    UPCOMING
                                </span>
                            )}
                            <h3 className="webinar-modal__title">{title}</h3>
                            <div className="webinar-modal__meta">
                                <span className="webinar-card__meta-item">
                                    <span className="wb-svg-icon-16">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="#5a6a7a" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2"></rect>
                                            <line x1="16" y1="2" x2="16" y2="6"></line>
                                            <line x1="8" y1="2" x2="8" y2="6"></line>
                                            <line x1="3" y1="10" x2="21" y2="10"></line>
                                        </svg>
                                    </span>
                                    <span>{startDateFormat}{endTimeFormat ? ` - ${endTimeFormat}` : ''} {tzAbbr}</span>
                                </span>
                                {durationTimes && (
                                    <span className="webinar-card__meta-item">
                                        <span className="wb-svg-icon-16">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="#5a6a7a" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <polyline points="12,6 12,12 16,14"></polyline>
                                            </svg>
                                        </span>
                                        <span>{durationTimes.hours}h {durationTimes.minutes}m</span>
                                    </span>
                                )}
                            </div>
                            <WebinarTags tags={tags} isModal={true} />
                            <div className="webinar-modal__description" dangerouslySetInnerHTML={this.createMarkup(raw_description)}></div>
                        </div>
                    </div>
                )}

            </article>)
    }
}

export default WebinarsListItem;



// WEBPACK FOOTER //
// ./src/components/Webinars/WebinarsListItem.js